import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ListTodo, Plus, Loader2, User, Truck, Paperclip, Upload, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo

function formatBytes(b) {
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
    return `${(b / 1024 / 1024).toFixed(1)} Mo`;
}

export default function TacheExternePage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [invalid, setInvalid] = useState(false);
    const [info, setInfo] = useState(null);

    const [desc, setDesc] = useState('');
    const [assignee, setAssignee] = useState('');
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [creating, setCreating] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/taches-effectuer/external/${token}`);
                setInfo(data);
            } catch {
                setInvalid(true);
            } finally { setLoading(false); }
        })();
    }, [token]);

    const assignables = info
        ? [...info.users, ...(info.has_demenageurs ? [{ id: 'demenageur', nom: 'Déménageur', role: 'demenageur', type: 'demenageur' }] : [])]
        : [];

    const handleFiles = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        if (files.length + incoming.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} fichiers par tâche`);
            e.target.value = '';
            return;
        }
        for (const f of incoming) {
            if (f.size > MAX_FILE_SIZE) {
                toast.error(`Fichier "${f.name}" > 10 Mo`);
                e.target.value = '';
                return;
            }
        }
        setFiles(prev => [...prev, ...incoming]);
        e.target.value = '';
    };

    const submit = async () => {
        if (!desc.trim()) return toast.error('Description requise');
        if (!assignee) return toast.error('Sélectionner un destinataire');
        const target = assignables.find(u => u.id === assignee);
        if (!target) return toast.error('Destinataire introuvable');
        setCreating(true);
        try {
            const fd = new FormData();
            fd.append('description', desc.trim());
            fd.append('assignee_id', target.id);
            fd.append('assignee_nom', target.nom);
            fd.append('assignee_type', target.type);
            fd.append('assignee_role', target.role || '');
            for (const f of files) fd.append('files', f, f.name);
            await axios.post(`${API}/api/taches-effectuer/external/${token}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSubmitted(true);
            setDesc(''); setAssignee(''); setFiles([]);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally { setCreating(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    if (invalid) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <Card className="max-w-md w-full border-red-200 bg-white shadow-md" data-testid="tache-externe-invalid">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Lien invalide</h2>
                        <p className="text-slate-600 text-sm">Ce lien n'est pas reconnu. Contactez l'administrateur.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <Card className="max-w-md w-full border-emerald-200 bg-white shadow-md" data-testid="tache-externe-success">
                    <CardContent className="p-8 text-center">
                        <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Tâche créée !</h2>
                        <p className="text-slate-600 text-sm">La tâche a été enregistrée au nom de <span className="font-semibold">{info.createur_nom}</span>.</p>
                        <Button className="mt-6 bg-[#2e7cd6] hover:bg-[#2568b8] text-white" onClick={() => setSubmitted(false)} data-testid="tache-externe-new-btn">
                            <Plus size={16} className="mr-2" /> Ajouter une autre tâche
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="tache-externe-page">
            <div className="max-w-xl mx-auto">
                <div className="mb-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                        <ListTodo size={22} className="text-[#2e7cd6]" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Nouvelle tâche</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Créée par <span className="font-semibold text-slate-700" data-testid="tache-externe-createur">{info.createur_nom}</span>
                    </p>
                </div>

                <Card className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <CardContent className="p-5 space-y-4">
                        <div>
                            <label className="text-xs text-slate-600 mb-1 block">Description *</label>
                            <Textarea
                                value={desc}
                                onChange={e => setDesc(e.target.value)}
                                rows={4}
                                placeholder="Décrire la tâche à effectuer..."
                                className="resize-none"
                                data-testid="tache-externe-desc"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-600 mb-1 block">Assigner à *</label>
                            <Select value={assignee} onValueChange={setAssignee}>
                                <SelectTrigger data-testid="tache-externe-assignee">
                                    <SelectValue placeholder="Choisir un destinataire" />
                                </SelectTrigger>
                                <SelectContent>
                                    {info.users.length > 0 && (
                                        <>
                                            <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Utilisateurs</div>
                                            {info.users.map(u => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    <span className="flex items-center gap-2">
                                                        <User size={13} className="text-slate-400" /> {u.nom}
                                                        <span className="text-[10px] text-slate-400">({u.role})</span>
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </>
                                    )}
                                    {info.has_demenageurs && (
                                        <SelectItem value="demenageur">
                                            <span className="flex items-center gap-2">
                                                <Truck size={13} className="text-violet-500" /> Déménageur
                                            </span>
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <label className="text-xs text-slate-600 mb-1 flex items-center gap-1">
                                <Paperclip size={12} className="text-slate-500" />
                                Fichiers / Photos (optionnel)
                            </label>
                            <p className="text-[11px] text-slate-400 mb-2">Jusqu'à {MAX_FILES} fichiers · max 10 Mo chacun</p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                className="hidden"
                                onChange={handleFiles}
                                data-testid="tache-externe-files-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                className="border-dashed border-slate-300 text-slate-600 hover:bg-slate-50"
                                data-testid="tache-externe-files-pick-btn"
                            >
                                <Upload size={14} className="mr-1.5" />
                                Choisir un fichier ou une photo
                            </Button>
                            {files.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                    {files.map((f, i) => (
                                        <li key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5 text-sm" data-testid={`tache-externe-file-${i}`}>
                                            <Paperclip size={12} className="text-slate-400 shrink-0" />
                                            <span className="flex-1 truncate text-slate-700 text-xs">{f.name}</span>
                                            <span className="text-[10px] text-slate-400 shrink-0">{formatBytes(f.size)}</span>
                                            <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0" title="Retirer">
                                                <X size={12} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <Button
                            className="w-full bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                            onClick={submit}
                            disabled={creating}
                            data-testid="tache-externe-submit"
                        >
                            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus size={16} className="mr-1.5" />}
                            Créer la tâche
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
